import React from "react";
import {
    BaseEdge,
    EdgeLabelRenderer,
    getBezierPath,
    useStore,
} from "reactflow";
import { Position } from "reactflow";

// Compute the center of a node from node internals
export function getNodeCenter(node) {
    // node.internals.positionAbsolute is used by recent versions; fallback to measured/positionAbsolute
    const posAbs = node?.internals?.positionAbsolute ||
        node?.measured?.positionAbsolute ||
        node?.positionAbsolute || { x: 0, y: 0 };
    const width = node?.measured?.width ?? node?.width ?? 0;
    const height = node?.measured?.height ?? node?.height ?? 0;
    return {
        x: posAbs.x + width / 2,
        y: posAbs.y + height / 2,
        width,
        height,
        posAbs,
    };
}

// Returns the intersection point of the line between centers and the rectangle bounds of the node
export function getIntersectionPoint({ source, target }) {
    const { x: sx, y: sy } = getNodeCenter(source);
    const { x: tx, y: ty, width, height, posAbs } = getNodeCenter(target);

    const dx = tx - sx;
    const dy = ty - sy;

    // Avoid division by zero
    const m = Math.abs(dx) > 0.0001 ? dy / dx : Number.POSITIVE_INFINITY;

    // Rectangle sides
    const left = posAbs.x;
    const right = posAbs.x + width;
    const top = posAbs.y;
    const bottom = posAbs.y + height;

    // Candidate intersections
    const points = [];

    // Intersect with left/right
    if (Number.isFinite(m)) {
        const yAtLeft = m * (left - sx) + sy;
        if (yAtLeft >= top && yAtLeft <= bottom)
            points.push({ x: left, y: yAtLeft, side: Position.Left });
        const yAtRight = m * (right - sx) + sy;
        if (yAtRight >= top && yAtRight <= bottom)
            points.push({ x: right, y: yAtRight, side: Position.Right });
    }

    // Intersect with top/bottom
    if (Math.abs(m) > 0) {
        const xAtTop = (top - sy) / m + sx;
        if (xAtTop >= left && xAtTop <= right)
            points.push({ x: xAtTop, y: top, side: Position.Top });
        const xAtBottom = (bottom - sy) / m + sx;
        if (xAtBottom >= left && xAtBottom <= right)
            points.push({ x: xAtBottom, y: bottom, side: Position.Bottom });
    } else {
        // Horizontal line
        points.push({
            x: dx > 0 ? left : right,
            y: sy,
            side: dx > 0 ? Position.Left : Position.Right,
        });
    }

    // Pick the point that is closest to the source center along the line direction towards target
    if (!points.length) return { x: tx, y: ty, side: Position.Top };
    const sorted = points.sort((a, b) => {
        const da = (a.x - sx) ** 2 + (a.y - sy) ** 2;
        const db = (b.x - sx) ** 2 + (b.y - sy) ** 2;
        return da - db;
    });
    return sorted[0];
}

// Build edge params used by FloatingEdge
export function getEdgeParams(sourceNode, targetNode) {
    const sourceCenter = getNodeCenter(sourceNode);
    const targetCenter = getNodeCenter(targetNode);

    const srcIntersect = getIntersectionPoint({
        source: targetNode,
        target: sourceNode,
    });
    const tgtIntersect = getIntersectionPoint({
        source: sourceNode,
        target: targetNode,
    });

    return {
        sx: srcIntersect.x,
        sy: srcIntersect.y,
        tx: tgtIntersect.x,
        ty: tgtIntersect.y,
        sourcePos: srcIntersect.side,
        targetPos: tgtIntersect.side,
    };
}

// Helper used by the connection line (mouse is the "target node")
export function getParamsForConnection(sourceNode, mouseX, mouseY) {
    const dummyTarget = {
        internals: { positionAbsolute: { x: mouseX, y: mouseY } },
        measured: { width: 0, height: 0 },
        width: 0,
        height: 0,
    };
    const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(
        sourceNode,
        dummyTarget
    );
    return { sx, sy, tx, ty, sourcePos, targetPos };
}

function balancedOffsetStep(idx) {
    if (idx === 0) return 0;
    const step = Math.ceil(idx / 2);
    return idx % 2 === 1 ? step : -step;
}

export const FloatingEdge = ({
    id,
    source,
    target,
    markerEnd,
    markerStart, // comment: enables the extra arrow when reciproca is true
    style,
    label, // comment: subtle relationship "tipo"
    data,
}) => {
    const sourceNode = useStore((s) => s.nodeInternals.get(source));
    const targetNode = useStore((s) => s.nodeInternals.get(target));

    if (!sourceNode || !targetNode) return null;

    const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(
        sourceNode,
        targetNode
    );

    // comment: === minimal anti-overlap tweak ===
    const idx = data?.parallelIndex ?? 0; // comment: 0 for first, 1 for second, etc. (from NodosBoard.jsx)
    const GAP = 18; // comment: tweak spacing here if needed
    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.hypot(dx, dy) || 1; // comment: avoid division by zero
    const ux = -dy / len; // comment: unit perpendicular (x)
    const uy = dx / len; // comment: unit perpendicular (y)
    const k = balancedOffsetStep(idx) * GAP; // comment: signed magnitude

    // comment: shift both endpoints in the same perpendicular direction
    const sxx = sx + ux * k;
    const syy = sy + uy * k;
    const txx = tx + ux * k;
    const tyy = ty + uy * k;

    const [edgePath] = getBezierPath({
        sourceX: sxx,
        sourceY: syy,
        targetX: txx,
        targetY: tyy,
        sourcePosition: sourcePos,
        targetPosition: targetPos,
    });

    const midX = (sxx + txx) / 2;
    const midY = (syy + tyy) / 2 - 10;

    return (
        <>
            <BaseEdge
                id={id}
                path={edgePath}
                markerEnd={markerEnd}
                markerStart={markerStart}
                style={style}
            />

            {label ? (
                <EdgeLabelRenderer>
                    <div
                        style={{
                            position: "absolute",
                            transform: `translate(-50%, -50%) translate(${midX}px, ${midY}px)`,
                            pointerEvents: "none",
                            fontSize: 11,
                            lineHeight: 1,
                            opacity: 0.7,
                            background: "rgba(255,255,255,0.6)",
                            padding: "2px 6px",
                            borderRadius: 6,
                            boxShadow: "0 0 6px rgba(0,0,0,0.08)",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {label}
                    </div>
                </EdgeLabelRenderer>
            ) : null}
        </>
    );
};

export const FloatingConnectionLine = ({
    fromX,
    fromY,
    toX,
    toY,
    fromNode,
}) => {
    const sourceNode = useStore((s) => s.nodeInternals.get(fromNode?.id));
    if (!sourceNode) return null;

    const { sx, sy, tx, ty, sourcePos, targetPos } = getParamsForConnection(
        sourceNode,
        toX,
        toY
    );

    const [edgePath] = getBezierPath({
        sourceX: sx ?? fromX,
        sourceY: sy ?? fromY,
        targetX: tx ?? toX,
        targetY: ty ?? toY,
        sourcePosition: sourcePos,
        targetPosition: targetPos,
    });

    return (
        <g>
            <path
                fill="none"
                d={edgePath}
                stroke="currentColor"
                strokeWidth={1.5}
            />
        </g>
    );
};
